// Shared helpers for the domiciliary schedule screen.
import { localTime } from '../../../utils/datetime';
import { unavailabilityLabel } from '../../../utils/leaveTypes';

export const PX_PER_HOUR = 160; // timeline density (wide enough to read the client name on each card)

// Baseline window; the real window grows to fit the data (see computeWindow).
const BASE_START = 6;
const BASE_END = 22;

// "07:30" -> 450 (minutes since midnight)
export const toMinutes = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h)) return null;
  return h * 60 + (m || 0);
};

// Work out the visible hour window from everything on screen so nothing is
// clipped: earliest start … latest end, then ONE HOUR MORE of padding at the
// end (and start no later than 06:00 to stay familiar). Falls back to 06–22.
export const computeWindow = (zones = [], unassigned = []) => {
  const times = [];
  const push = (s, e) => {
    const a = toMinutes(s); const b = toMinutes(e);
    if (a != null) times.push(a);
    if (b != null) times.push(b);
  };
  zones.forEach((z) => (z.carers || []).forEach((c) => {
    if (c.shift) push(c.shift.start, c.shift.end);
    (c.extra_availability || []).forEach((ea) => push(ea.start, ea.end));
    (c.visits || []).forEach((v) => push(v.start, v.end));
  }));
  (unassigned || []).forEach((v) => push(v.start, v.end));

  if (times.length === 0) return { startHour: BASE_START, endHour: BASE_END };
  const startHour = Math.max(0, Math.min(Math.floor(Math.min(...times) / 60), BASE_START));
  const endHour = Math.min(24, Math.max(Math.ceil(Math.max(...times) / 60) + 1, BASE_END));
  return { startHour, endHour };
};

// Hour labels for a window, e.g. [6,7,...,23].
export const hoursOf = (win) => Array.from(
  { length: win.endHour - win.startHour + 1 },
  (_, i) => win.startHour + i
);

// Left/width as % of the timeline window, clamped to the visible range.
export const blockGeometry = (start, end, win) => {
  const winStart = win.startHour * 60;
  const winMinutes = (win.endHour - win.startHour) * 60;
  let s = toMinutes(start);
  let e = toMinutes(end);
  if (s == null) return null;
  if (e == null || e <= s) e = s + 45; // default 45-min block
  s = Math.max(s, winStart);
  e = Math.min(e, winStart + winMinutes);
  if (e <= s) return null;
  return {
    left: `${((s - winStart) / winMinutes) * 100}%`,
    width: `${((e - s) / winMinutes) * 100}%`,
  };
};

// "Now" marker position (% of window) or null if outside the window / not today.
export const nowGeometry = (dateStr, todayStr, win) => {
  if (dateStr !== todayStr) return null;
  const winStart = win.startHour * 60;
  const winMinutes = (win.endHour - win.startHour) * 60;
  const now = new Date();
  const mins = now.getHours() * 60 + now.getMinutes();
  if (mins < winStart || mins > winStart + winMinutes) return null;
  return `${((mins - winStart) / winMinutes) * 100}%`;
};

// ── Timeline scale ──────────────────────────────────────────────────────────
// Visit cards flow inside a carer's shift band, so a SHORT shift (e.g. one hour)
// can't hold them at a readable size. We widen the hour columns that a short
// busy shift spans so the cards fit. The whole board (ruler, gridlines, bands,
// markers) shares this one scale, and the ruler labels are left-aligned to each
// column's start, so the widened gap lands exactly on the shift's hour(s).
// Returns one px width per hour column (length = endHour - startHour).
const CARD_MIN = 185; // px each visit card needs to stay legible
const cardsFlowWidth = (n) => (n > 0 ? 16 + n * CARD_MIN + (n - 1) * 8 : 0);

export const computeHourWidths = (win, zones = []) => {
  const cols = win.endHour - win.startHour;
  const widths = Array.from({ length: Math.max(cols, 0) }, () => PX_PER_HOUR);
  if (cols <= 0) return widths;
  zones.forEach((z) => (z.carers || []).forEach((c) => {
    const visitCount = (c.visits || []).length;
    if (!c.shift || visitCount === 0) return;
    const s = toMinutes(c.shift.start);
    const e = toMinutes(c.shift.end);
    if (s == null || e == null || e <= s) return;
    const startH = Math.max(win.startHour, Math.floor(s / 60));
    const endH = Math.min(win.endHour, Math.ceil(e / 60));
    const span = endH - startH;
    if (span <= 0) return;
    const perHour = cardsFlowWidth(visitCount) / span; // px each spanned hour needs
    for (let h = startH; h < endH; h += 1) {
      const idx = h - win.startHour;
      if (idx >= 0 && idx < cols) widths[idx] = Math.max(widths[idx], perHour);
    }
  }));
  return widths;
};

// A time→x scale over the hour widths. Positions are returned as PERCENTAGES of
// the total timeline so they stay aligned with the flex-grown ruler columns
// whatever the board's rendered width.
export const makeXScale = (win, hourWidths) => {
  const winStart = win.startHour * 60;
  const offsets = [0];
  hourWidths.forEach((w, i) => { offsets[i + 1] = offsets[i] + w; });
  const totalWidth = offsets[offsets.length - 1] || 1;
  const xOf = (minutes) => {
    if (minutes == null) return null;
    const m = Math.max(winStart, Math.min(minutes, win.endHour * 60));
    const rel = m - winStart;
    const hourIdx = Math.min(Math.floor(rel / 60), hourWidths.length - 1);
    const within = (rel - hourIdx * 60) / 60;
    return offsets[hourIdx] + within * hourWidths[hourIdx];
  };
  const pct = (px) => `${(px / totalWidth) * 100}%`;
  const blockGeo = (start, end) => {
    let s = toMinutes(start);
    let e = toMinutes(end);
    if (s == null) return null;
    if (e == null || e <= s) e = s + 45; // default 45-min block
    const x1 = xOf(s);
    const x2 = xOf(e);
    if (x2 <= x1) return null;
    return { left: pct(x1), width: pct(x2 - x1) };
  };
  return { totalWidth, xOf, pct, blockGeo };
};

// Visit status → Tailwind classes for the block.
export const STATUS_STYLES = {
  scheduled:   { block: 'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-900/20 dark:border-blue-900/50 dark:text-blue-400',     bar: 'bg-blue-500',    label: 'Scheduled' },
  in_progress: { block: 'bg-sky-50 border-sky-400 text-sky-900',        bar: 'bg-sky-500',     label: 'In progress' },
  completed:   { block: 'bg-emerald-50 border-emerald-300 text-emerald-900 dark:bg-emerald-900/10 dark:border-emerald-900/50 dark:text-emerald-400', bar: 'bg-emerald-500', label: 'Done' },
  missed:      { block: 'bg-rose-50 border-rose-300 text-rose-900',     bar: 'bg-rose-500',    label: 'Missed' },
};

export const statusStyle = (s) => STATUS_STYLES[s] || STATUS_STYLES.scheduled;

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// ── Review system (mirrors the duty roster) ────────────────────────────────
// A visit needs review when it was missed (scheduled, past, no clock-in) or
// when the actual clocked time DIFFERS from the scheduled time. Resolving it
// (recording hours / marking time away) sets `__reviewed` / removes it.
export const visitReviewKind = (v) => {
  // __reviewed = resolved in the current draft; reviewed = persisted on the
  // server (manager already approved/recorded). Either keeps it out of the queue.
  if (!v || v.__reviewed || v.reviewed) return null;
  if (v.status === 'missed') return 'missed';
  if (v.actual_start) {
    const aStart = localTime(v.actual_start);
    const aEnd = v.actual_end ? localTime(v.actual_end) : null;
    if (aStart !== v.start || (aEnd && v.end && aEnd !== v.end)) return 'differs';
  }
  return null;
};

// Flat list of items needing review across the (draft-applied) zones.
export const collectReviews = (zones = []) => {
  const items = [];
  zones.forEach((z) => (z.carers || []).forEach((c) => (c.visits || []).forEach((v) => {
    const kind = visitReviewKind(v);
    if (kind) items.push({ zone: z, carer: c, visit: v, kind });
  })));
  return items;
};

export const reviewCountForZone = (zone) => collectReviews([zone]).length;

// Flatten the schedule's zone→carer structure into a unique carer list (for the
// modals' carer pickers). Avoids a second API call.
export const carersFromSchedule = (zones = []) => {
  const map = new Map();
  zones.forEach((z) => (z.carers || []).forEach((c) => { if (!map.has(c.id)) map.set(c.id, { id: c.id, name: c.name, initials: c.initials }); }));
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
};

// Why a carer CAN'T take a visit on drag-and-drop assign — returns a warning
// string, or null when the assignment is fine. Shared by the drop handler (to
// block + warn) and the carer row (to show a red instead of green drop border).
export const assignBlockReason = (carer, visit) => {
  if (!carer || !visit) return null;
  if (carer.time_away) {
    return `${carer.name} is on ${(unavailabilityLabel(carer.time_away) || 'leave').toLowerCase()} — can't assign`;
  }
  // Day off / not scheduled: no shift and no extra availability for the day.
  if (!carer.shift && !(carer.extra_availability && carer.extra_availability.length)) {
    return `${carer.name} is off today — add availability before assigning`;
  }
  if ((carer.visits || []).some((x) => String(x.assignment_id) === String(visit.assignment_id))) {
    return `${carer.name} is already on this visit`;
  }
  // The shift OR a one-off extra-availability window must FULLY cover the visit
  // time (matches the server rule in assignCarer). Otherwise it's a hard block.
  {
    const a0 = toMinutes(visit.start);
    if (a0 != null) {
      let b0 = toMinutes(visit.end);
      if (b0 == null || b0 <= a0) b0 = a0 + 45;
      const covers = (start, end) => {
        const s = toMinutes(start); let e = toMinutes(end);
        if (s == null || e == null) return false;
        if (e <= s) e += 1440;
        let a = a0; let b = b0; if (b <= a) b += 1440;
        return a >= s && b <= e;
      };
      const windows = [];
      if (carer.shift) windows.push([carer.shift.start, carer.shift.end]);
      (carer.extra_availability || []).forEach((ea) => windows.push([ea.start, ea.end]));
      if (!windows.some(([s, e]) => covers(s, e))) {
        return `${carer.name}'s shift doesn't cover ${visit.start}${visit.end ? `–${visit.end}` : ''}`;
      }
    }
  }
  const vS = toMinutes(visit.start);
  const vE = toMinutes(visit.end) ?? (vS != null ? vS + 45 : null);
  const clash = (carer.visits || []).find((x) => {
    const xS = toMinutes(x.start);
    const xE = toMinutes(x.end) ?? (xS != null ? xS + 45 : null);
    return vS != null && xS != null && vE != null && xE != null && vS < xE && xS < vE;
  });
  if (clash) {
    return `${carer.name} already has a visit at ${clash.start}${clash.end ? `–${clash.end}` : ''}`;
  }
  return null;
};
